"use client";

// @ubm/ui/overlays — L'ENGAGEMENT D'HÔTE DES COUCHES (lot U2, 05/10/2026).
//
// Un hôte (une fenêtre du Bureau, une surface affichée au téléphone) dit s'il est ENGAGÉ : au premier plan ET visible.
// Toute couche rendue sous lui (Dialog, Select, popup, peek, menu) lit cet état par contexte — et le contexte React
// traverse les portails, donc une liste portée sous `<body>` reste rattachée à sa fenêtre.
//
// Contrat fixé, communiqué à U1 (Select) et U3 :
//   · `OverlayHostProvider({ engaged, children })` — un hôte imbriqué n'est engagé que si son parent l'est aussi ;
//   · `useOverlayHostEngaged(): boolean` — `true` sans fournisseur : hors Bureau (route classique), rien ne change.
//
// Une couche d'un hôte DÉSENGAGÉ reste montée (état ouvert logique, brouillons) mais se SUSPEND : ni piège, ni inertie,
// ni verrou de défilement, ni écoute clavier ou clic-extérieur, ni rappel métier (`onOpenChange`/`onClose`) induit, ni
// vol de focus. Sa racine porte `data-ubm-overlay-engaged="false"` et sort du classement de la pile commune
// (`estOverlayDuDessus`). Engagée, la racine ne porte AUCUN attribut de plus : DOM strictement inchangé.
//
// LE PONT D'ÉCHAP (« cédé ≠ consommé »). Une couche Radix ne peut refuser sa propre fermeture qu'en appelant
// `preventDefault()` sur l'Échap — ce qui, pour toutes les autres couches, veut dire « déjà consommé ». Quand une couche
// Radix refuse parce qu'une couche non Radix est au-dessus d'elle, ou parce que son hôte est désengagé, elle CÈDE
// l'événement (`cederEchap`) : il reste empêché pour Radix, mais `echapConsomme` le lit comme libre, et la couche qui le
// prend le CONSOMME (`consommerEchap`) pour tout ce qui suit. Aucune pile ni propriétaire d'Échap secondaire : la seule
// marque d'état est l'identité de l'événement natif.
import { createContext, useContext, type ReactNode } from "react";

/** Posé (valeur `"false"`) sur la racine d'une couche dont l'hôte est désengagé. Absent sinon. */
export const ATTRIBUT_ENGAGEMENT = "data-ubm-overlay-engaged";

const OverlayHostContext = createContext<boolean>(true);

export interface OverlayHostProviderProps {
  /** L'hôte est-il au premier plan ET visible ? */
  engaged: boolean;
  children?: ReactNode;
}

/** Déclare l'engagement d'un hôte. Imbriqué : engagé seulement si l'hôte parent l'est aussi. */
export function OverlayHostProvider({ engaged, children }: OverlayHostProviderProps) {
  const parent = useContext(OverlayHostContext);
  return <OverlayHostContext.Provider value={parent && engaged}>{children}</OverlayHostContext.Provider>;
}

/** L'hôte courant est-il engagé ? `true` sans fournisseur (hors Bureau). */
export function useOverlayHostEngaged(): boolean {
  return useContext(OverlayHostContext);
}

/** À étaler sur la racine d'une couche : rien quand engagée, `data-ubm-overlay-engaged="false"` sinon. */
export function attributsEngagement(engaged: boolean): { [ATTRIBUT_ENGAGEMENT]?: "false" } {
  return engaged ? {} : { [ATTRIBUT_ENGAGEMENT]: "false" };
}

/** `el` appartient-il à un hôte engagé ? (ni lui ni un ancêtre DOM ne porte l'attribut de désengagement) */
export function estCoucheEngagee(el: Element): boolean {
  return el.closest(`[${ATTRIBUT_ENGAGEMENT}="false"]`) === null;
}

const echapsCedes = new WeakSet<Event>();

/**
 * CÈDE un Échap : l'empêche (une couche Radix ne se ferme pas) sans le consommer pour les autres couches.
 * À appeler depuis `onEscapeKeyDown` d'une couche Radix qui n'est pas celle du dessus, ou dont l'hôte est désengagé.
 */
export function cederEchap(event: Event): void {
  if (event.defaultPrevented && !echapsCedes.has(event)) return; // déjà consommé : rien à céder
  echapsCedes.add(event);
  event.preventDefault();
}

/** L'Échap a-t-il déjà été CONSOMMÉ par une couche ? Un Échap cédé ne l'est pas. */
export function echapConsomme(event: Event): boolean {
  return event.defaultPrevented && !echapsCedes.has(event);
}

/** CONSOMME un Échap : la couche qui se ferme le marque pour toutes les couches qui écoutent après elle. */
export function consommerEchap(event: Event): void {
  echapsCedes.delete(event);
  event.preventDefault();
}
